import { Card, CardHeader, CardTitle, CardBody, Badge, Chip } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Layers } from 'lucide-react';
import { formatMoney } from '@/lib/format';
import { useServiceConfigoptions, type ServiceConfigOption } from '@/api/services';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

/**
 * Read-only peek table for configoptions on the Statement tab.
 * Shows 8 columns matching the legacy CDN/Conversant peek_settings:
 *   name, commitment_value, statementable, commitment_available,
 *   commitment_time, commitment_prepaid, commitment_price_per_unit, commitment_price_total
 *
 * Inline edit is out of scope for this pass (noted in report).
 */
export function StatementConfigoptionPeekTable({ id, currency = 'VND' }: { id: number; currency?: string }) {
  const configs = useServiceConfigoptions(id);

  if (configs.isError)
    return <TabError title="Failed to load configoptions" onRetry={() => configs.refetch()} />;
  if (configs.isLoading) return <TabLoading rows={3} />;

  const rows = configs.data?.results ?? [];

  const muted = { color: 'var(--vn-fg-muted)' } as const;
  const right: React.CSSProperties = { display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  const columns: ColumnDef<ServiceConfigOption, unknown>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
          {row.original.name}
        </span>
      ),
    },
    {
      id: 'commitment_value',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Commitment</span>,
      cell: ({ row }) => {
        const v = row.original.commitment_value;
        return (
          <span style={right}>
            {v != null ? `${v} ${row.original.unit ?? ''}`.trim() : <span style={muted}>—</span>}
          </span>
        );
      },
    },
    {
      id: 'statementable',
      header: 'Statementable',
      cell: ({ row }) => {
        const s = row.original.statementable;
        if (s == null) return <span style={muted}>—</span>;
        return s
          ? <Badge tone="success">Yes</Badge>
          : <Badge tone="neutral">No</Badge>;
      },
    },
    {
      id: 'commitment_available',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Available</span>,
      cell: ({ row }) => {
        const v = row.original.commitment_available;
        return <span style={right}>{v != null ? v : <span style={muted}>—</span>}</span>;
      },
    },
    {
      id: 'commitment_time',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Commitment time</span>,
      cell: ({ row }) => {
        const v = row.original.commitment_time;
        return <span style={right}>{v != null ? v : <span style={muted}>—</span>}</span>;
      },
    },
    {
      id: 'commitment_prepaid',
      header: 'Prepaid',
      cell: ({ row }) => {
        const p = row.original.commitment_prepaid;
        if (p == null) return <span style={muted}>—</span>;
        return p
          ? <Chip tone="accent" appearance="subtle" size="sm">Prepaid</Chip>
          : <Chip tone="neutral" appearance="subtle" size="sm">Postpaid</Chip>;
      },
    },
    {
      id: 'commitment_price_per_unit',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Price / unit</span>,
      cell: ({ row }) => (
        <span style={right}>{formatMoney(row.original.commitment_price_per_unit, currency)}</span>
      ),
    },
    {
      id: 'commitment_price_total',
      header: () => <span style={{ display: 'block', textAlign: 'right' }}>Total price</span>,
      cell: ({ row }) => (
        <span style={right}>{formatMoney(row.original.commitment_price_total, currency)}</span>
      ),
    },
  ];

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Configoptions (statement view)</CardTitle>
      </CardHeader>
      <CardBody style={{ padding: 'var(--vn-space-0)' }}>
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.service_configoption_id)}
          emptyState={
            <EmptyState
              icon={<Layers size={24} />}
              title="No configoptions"
              description="This service has no configoptions configured."
            />
          }
        />
      </CardBody>
    </Card>
  );
}
